import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggerService } from '@klickdata/core/application';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService } from '@klickdata/core/mobile';
import { Resource, ResourceSocketService } from '@klickdata/core/resource';
import { ResourceActionHandlerService } from '@klickdata/core/resource/src/resource-action-handler.service';
import { OccasionDate, PlayableResourceSpec } from '@klickdata/core/resource/src/resource.model';
import { ResourceTypes } from '@klickdata/core/resource/src/types.enum';
import {
    Filter,
    GlobalFilterProperty,
    GlobalFilterPropertyType,
    SelectFilterOption,
    TableFilterComponent,
} from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { ResourceTableBaseComponent } from '@klickdata/shared-components/src/resource-table-base/resource-table-base.component';
import { ShareService } from '@klickdata/shared-components/src/share/share.service';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { BehaviorSubject, filter, Observable, takeUntil } from 'rxjs';
import { ResourceEnrollmentSheetComponent } from '../resource-assignment/resource-enrollment-sheet/resource-enrollment-sheet.component';

@Component({
    selector: 'app-instructor-enrollments-listing',
    templateUrl: './instructor-enrollments-listing.component.html',
    styleUrls: ['./instructor-enrollments-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstructorEnrollmentsListingComponent extends OnDestroyHandler implements AfterViewInit, OnInit {
    @ViewChild('tableBase') tableBase: ResourceTableBaseComponent;
    @Input() tableFilterItems: Filter<any>[];
    @Input() user: User;
    @Output() onSelected: EventEmitter<number> = new EventEmitter<number>();
    @ViewChild('title', { static: true }) title: TemplateRef<any>;
    @ViewChild('img', { static: true }) img: TemplateRef<any>;
    @ViewChild('enrollment', { static: true }) enrollment: TemplateRef<any>;
    @ViewChild('approved_users', { static: true }) approved_users: TemplateRef<any>;
    @ViewChild('pending_users', { static: true }) pending_users: TemplateRef<any>;
    @ViewChild('dismissed_users', { static: true }) dismissed_users: TemplateRef<any>;
    @ViewChild('type', { static: true }) type: TemplateRef<any>;
    @ViewChild('enrollment_date', { static: true }) enrollment_date: TemplateRef<any>;
    @ViewChild('expansionContextTemplate', { static: true }) expansionContextTemplate: TemplateRef<any>;
    public approvalStatusSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
    @ViewChild('tools', { static: true }) tools: TemplateRef<any>;
    public resourceTypeOptions: SelectFilterOption[];
    public shareLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    GlobalFilterPropertyType = GlobalFilterPropertyType;
    public isMobile$: Observable<boolean>;
    public customer$: Observable<Customer>;
    @Input() public expandedRowId: number | null;
    GlobalFilterProperty = GlobalFilterProperty;
    columnsData: { [key: string]: TemplateRef<any> } = {};
    otherContext: { [key: string]: TemplateRef<any> } = {};
    public approvalStatusOptions: SelectFilterOption[];
    columnsKeys: any = {
        img: { label: $localize`Image`, sortable: false },
        title: { label: $localize`Title`, sortable: true },
        // enrollment: { label: $localize`Enrollment`, sortable: true },
        approved_users: { label: $localize`Approved`, sortable: true, icon: 'check_circle' },
        dismissed_users: { label: $localize`Dismissed`, sortable: true, icon: 'cancel' },
        pending_users: { label: $localize`Pending`, sortable: true, icon: 'pending_actions' },
        type: { label: $localize`Type`, sortable: false },
    };
    ResourceTypes = ResourceTypes;
    expandedFn = (resource: Resource) => resource.id === this.expandedRowId;
    private resourceActionHandlerService = inject(ResourceActionHandlerService);

    constructor(
        protected downloadHelper: DownloadHelperService,
        public shareService: ShareService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected cdr: ChangeDetectorRef,
        protected mobile: MobileService,
        protected auth: AuthService,
        protected resourceSocketService: ResourceSocketService,
        protected bottomSheet: MatBottomSheet,
        protected messageService: MessageService,
        protected logger: LoggerService
    ) {
        super();

        this.resourceTypeOptions = [
            { title: $localize`Course`, value: 3, icon: 'kd-icon-task', isMainResource: true },
            { title: $localize`Event`, value: 25, icon: 'event', isMainResource: false },
        ];
        this.isMobile$ = mobile.isMobile();
        this.approvalStatusOptions = Utils.getResApprovalStatusOptions();
    }
    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                filter((params) => params.expand),
                takeUntil(this.destroy)
            )
            .subscribe((params) => {
                setTimeout(() => {
                    this.expandedRowId = +params.expand;
                    this.cdr.markForCheck();
                }, 500);
            });
        this.customer$ = this.auth.getCustomer();
    }
    ngAfterViewInit(): void {
        this.columnsData['img'] = this.img;
        this.columnsData['title'] = this.title;
        this.columnsData['type'] = this.type;
        let actionSocket: Observable<Resource>;
        this.columnsData['enrollment'] = this.enrollment;
        this.columnsKeys['enrollment'] = { label: $localize`Enrollment`, icon: 'app_registration', sortable: true };
        actionSocket = this.resourceSocketService.listenToNewResource('EnrolledResourceEvent');
        actionSocket.pipe(takeUntil(this.destroy)).subscribe((resource) => {
            this.logger.log('actionSocket', resource);
            this.tableBase.dataSource.replace(resource);
        });
        this.columnsData['approved_users'] = this.approved_users;
        this.columnsData['dismissed_users'] = this.dismissed_users;
        this.columnsData['pending_users'] = this.pending_users;
        this.columnsData['tools'] = this.tools;
        this.otherContext['expansionContext'] = this.expansionContextTemplate;
    }
    public playFromOccasion(params: {
        occasion: OccasionDate;
        resource: Resource;
        playableResourceCheck: PlayableResourceSpec;
    }): void {
        this.resourceActionHandlerService
            .playResource({
                resource: params.resource,
                resPlayableSpec: params.playableResourceCheck,
            })
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (res) {
                    this.tableBase.dataSource.refresh();
                }
            });
    }
    addEnrollment(resource: any) {
        this.bottomSheet
            .open(ResourceEnrollmentSheetComponent, {
                data: { resource: resource, customer_id: resource.customer_id },
                panelClass: 'sheet-wrapper',
            })
            .afterDismissed()
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (res) {
                    this.onChangeStatus();
                    this.messageService.openMessage(MessageSavedComponent, $localize`Enrollment updated successfully`);
                }
            });
    }
    public getPlayBtnTxt(resource: Resource): string {
        return resource.opportunity_id ? $localize`:@@continue:Continue` : $localize`:@@play:Play`;
    }
    public download(resource: Resource) {
        this.downloadHelper.downloadWithOptions(resource.downloads);
    }
    browse(resource: Resource) {
        this.router.navigate(['/home/dashboard/resource-details/' + resource.id]);
    }
    public getOccasionDates(resource: Resource): OccasionDate[] {
        const dates = resource.enrollmentRequiredMetadata?.occasion_dates;
        return !!dates?.length ? dates : [];
    }
    canExpand(resource: Resource) {
        return !!resource.enrollment_counts?.total || this.getOccasionDates(resource).length;
    }

    public play(resource: Resource, playableResourceCheck?: PlayableResourceSpec): void {
        this.resourceActionHandlerService
            .playResource({
                resource: resource,
                resPlayableSpec: playableResourceCheck,
            })
            .pipe(takeUntil(this.destroy))
            .subscribe();
    }

    onChangeStatus() {
        this.tableBase.dataSource.refresh();
    }
    edit(resource: Resource) {
        if (resource.type_id === ResourceTypes.GeneralCoursePlan) {
            this.user.isAdmin()
                ? this.router.navigate(['/admin/content/courses/', resource.id])
                : this.router.navigate(['/home/manage/course-plan/', resource.id]);
        } else {
            this.user.isAdmin()
                ? this.router.navigate([`/admin/content/${ResourceTypes.playerRoute(resource.type_id)}s/`, resource.id])
                : this.router.navigate([`/home/manage/${ResourceTypes.playerRoute(resource.type_id)}/`, resource.id]);
        }
    }
    expansionToggled(resource: Resource) {
        if (this.canExpand(resource)) {
            const expandId = resource.id;
            this.onSelected.emit(expandId);
            this.expandedRowId = this.expandedRowId === expandId ? null : expandId;
        }
    }
}
